import { watch, onUnmounted } from "vue";
import { useTracking, buildGaItem } from "~/composables/useTracking";
import {
  addRecentlyViewedProduct,
  addRecentlyViewedVariant,
} from "~/utils/recentlyViewed";

/* ====================================================================
 *  useProductTracking
 *  – GA4 view_item + Meta Pixel ViewContent
 *  – recently-viewed (product + variant)
 * ==================================================================== */
export function useProductTracking({
  product,
  currentVariant,
  selectedColor,
  selectedSize,
  priceToShow,
}) {
  const { ga: gaLog, pixel: mpTrack } = useTracking();

  /* ---- view_item / ViewContent ---- */
  const __sentViewItem = new Set();

  watch(
    () => ({
      pid: product.value?.id || "",
      vid: currentVariant.value?.id || "",
      c: selectedColor.value,
      s: selectedSize.value,
    }),
    ({ pid, vid, c, s }) => {
      if (!import.meta.client || !pid) return;

      const needColor = (product.value.colors || []).length > 0;
      const needSize = (product.value.sizes || []).length > 0;
      if ((needColor && !c) || (needSize && !s)) return;

      const key = `${pid}__${vid || c || ""}__${s || ""}`;
      if (__sentViewItem.has(key)) return;
      __sentViewItem.add(key);

      const price = Number(priceToShow.value || 0);

      const item = buildGaItem({
        product: product.value,
        variant: currentVariant.value,
        variantFallback: [c, s],
        price,
        quantity: 1,
      });

      gaLog("view_item", { currency: "UAH", value: price, items: [item] });

      mpTrack("ViewContent", {
        content_type: "product",
        content_ids: [String(pid)].filter(Boolean),
        content_name: String(product.value?.title || ""),
        currency: "UAH",
        value: price,
        contents: [{ id: String(pid), quantity: 1, item_price: price }].filter(
          (x) => x.id,
        ),
      });
    },
    { immediate: true },
  );

  /* ---- Recently viewed ---- */
  watch(
    () => product.value?.id,
    (id, prev) => {
      if (id && id !== prev) addRecentlyViewedProduct(id);
    },
    { immediate: true },
  );

  let rvTimer = null;
  watch(
    () => [selectedColor.value, selectedSize.value, product.value?.id],
    ([c, s, pid]) => {
      if (!pid) return;
      if (rvTimer) clearTimeout(rvTimer);
      rvTimer = setTimeout(() => {
        const needColor = (product.value.colors || []).length > 0;
        const needSize = (product.value.sizes || []).length > 0;
        if ((!needColor || c) && (!needSize || s))
          addRecentlyViewedVariant(pid, {
            color: String(c || ""),
            size: String(s || ""),
            variantId: String(currentVariant.value?.id || ""),
          });
      }, 400);
    },
    { immediate: true },
  );

  onUnmounted(() => {
    if (rvTimer) clearTimeout(rvTimer);
  });
}
